/**
 * Service-worker registration (slice 10).
 *
 * Registered at `/sw.js`, scope `/` — the SAME script URL the slice-10f
 * kill-switch used, deliberately (see public/sw.js for the Option-A rationale:
 * our worker inherits the eviction job so a browser that still carries
 * traccar-web's caches ends up clean).
 *
 * PROD ONLY. In dev, Vite's module graph and HMR must not be intercepted by a
 * worker, and there are no hashed /assets to cache. The worker is exercised
 * against a production build (`vite build` + `vite preview`) and on the deployed
 * origin — the only honest way to test a service worker.
 *
 * `updateViaCache: 'none'` so the browser never serves /sw.js from its HTTP
 * cache on the update check — it always revalidates the bytes, matching Caddy's
 * `Cache-Control: no-cache` on /sw.js. This is what lets a new worker (and, for a
 * straggler, the eviction) ship promptly.
 *
 * NOTE ON STRAGGLERS: a browser still controlled by traccar-web's worker never
 * runs this code — its navigation is answered from traccar's precache, so the
 * Raad SPA's scripts do not execute. Such a browser gets our worker via the
 * browser's own update check on /sw.js, not via this call. This registration is
 * for the clean / already-evicted / fresh browsers, where the SPA does run.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch(() => {
        // A failed registration must never break the app — the SPA works fully
        // without the worker (it is a progressive enhancement: offline shell +
        // install). Swallow, do not surface.
      });
  });
}
